(function () {
  const selector = '[data-process-wave]';
  const desktopMedia = window.matchMedia('(min-width: 990px)');

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function readCssNumber(element, variableName) {
    const value = window.getComputedStyle(element).getPropertyValue(variableName);
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getTitleVisualRect(titleArc) {
    if (!titleArc) return null;

    const titleText = titleArc.querySelector('text');
    if (titleText) {
      const rect = titleText.getBoundingClientRect();
      if (rect.width || rect.height) {
        return rect;
      }
    }

    return titleArc.getBoundingClientRect();
  }

  function findPointForX(path, targetX) {
    const totalLength = path.getTotalLength();
    let start = path.getPointAtLength(0);
    let end = path.getPointAtLength(totalLength);
    const clampedX = clamp(targetX, Math.min(start.x, end.x), Math.max(start.x, end.x));
    let low = 0;
    let high = totalLength;

    for (let index = 0; index < 32; index += 1) {
      const mid = (low + high) / 2;
      const point = path.getPointAtLength(mid);

      if (point.x < clampedX) {
        low = mid;
      } else {
        high = mid;
      }
    }

    return path.getPointAtLength((low + high) / 2);
  }

  function clearSectionLayout(section) {
    section.style.removeProperty('--fpw-step-width');
    section.style.removeProperty('--fpw-node-size');
    section.style.removeProperty('--fpw-title-arc-width');
    section.style.removeProperty('--fpw-description-width');
    section.style.removeProperty('--fpw-wave-offset-x');
    section.style.removeProperty('--fpw-wave-offset-y');

    const stepsWrap = section.querySelector('.fpw-steps');
    if (stepsWrap) {
      stepsWrap.style.removeProperty('height');
    }

    section.querySelectorAll('.fpw-step').forEach((step) => {
      step.style.removeProperty('--fpw-step-center-x');
      step.style.removeProperty('--fpw-node-center-y');
      step.style.removeProperty('--fpw-top-block-y');
      step.style.removeProperty('--fpw-bottom-block-y');
    });
  }

  function setSectionScale(section, timelineWidth, count) {
    const safeCount = Math.max(count, 1);
    const unit = timelineWidth / (safeCount + 0.8);
    const stepWidth = clamp(unit * 0.76, 128, 176);
    const circleSizeDesktop = readCssNumber(section, '--fpw-circle-size-desktop');
    const nodeSize = clamp(circleSizeDesktop || stepWidth * 0.58, 56, 140);
    const titleArcWidth = clamp(Math.max(stepWidth * 0.68, nodeSize * 1.42), 96, 132);
    const descriptionWidth = clamp(stepWidth * 0.94, 132, 170);

    section.style.setProperty('--fpw-step-width', stepWidth.toFixed(2) + 'px');
    section.style.setProperty('--fpw-node-size', nodeSize.toFixed(2) + 'px');
    section.style.setProperty('--fpw-title-arc-width', titleArcWidth.toFixed(2) + 'px');
    section.style.setProperty('--fpw-description-width', descriptionWidth.toFixed(2) + 'px');
  }

  function updateSection(section) {
    const timeline = section.querySelector('.fpw-timeline');
    const stepsWrap = section.querySelector('.fpw-steps');
    const wave = section.querySelector('.fpw-wave');
    const path = wave ? wave.querySelector('path') : null;
    const steps = Array.from(section.querySelectorAll('.fpw-step'));

    if (!timeline || !stepsWrap || !wave || !path || !steps.length) return;
    if (!desktopMedia.matches) {
      clearSectionLayout(section);
      return;
    }

    const timelineRect = timeline.getBoundingClientRect();
    const stepsRect = stepsWrap.getBoundingClientRect();
    const waveRect = wave.getBoundingClientRect();
    const viewBox = wave.viewBox && wave.viewBox.baseVal ? wave.viewBox.baseVal : null;

    if (!viewBox || !timelineRect.width || !waveRect.width || !waveRect.height) return;

    setSectionScale(section, timelineRect.width, steps.length);

    const titleGap = readCssNumber(section, '--fpw-label-circle-gap-desktop');
    const descriptionGap = Math.max(titleGap, 14);
    const horizontalPadding = clamp(timelineRect.width * 0.045, 28, 58);
    const usableWidth = Math.max(timelineRect.width - horizontalPadding * 2, 1);
    const waveOffsetY = readCssNumber(section, '--fpw-wave-offset-y');
    const waveTopInSteps = waveRect.top - stepsRect.top - waveOffsetY;
    const pathBounds = path.getBBox();
    const bounds = [];

    steps.forEach((step, index) => {
      const node = step.querySelector('.fpw-step__node');
      const topBlock = step.querySelector('.fpw-step__top');
      const bottomBlock = step.querySelector('.fpw-step__bottom');
      const topTitleArc = topBlock ? topBlock.querySelector('.fpw-step__title-arc') : null;
      const bottomTitleArc = bottomBlock ? bottomBlock.querySelector('.fpw-step__title-arc') : null;

      if (!node || !topBlock || !bottomBlock) return;

      const ratio = steps.length === 1 ? 0.5 : index / (steps.length - 1);
      const centerX = horizontalPadding + usableWidth * ratio;
      const targetX = (centerX / waveRect.width) * viewBox.width;
      const point = findPointForX(path, targetX);

      step.style.setProperty('--fpw-step-center-x', centerX.toFixed(2) + 'px');

      const nodeRect = node.getBoundingClientRect();
      const topRect = topBlock.getBoundingClientRect();
      const bottomRect = bottomBlock.getBoundingClientRect();
      const topTitleRect = getTitleVisualRect(topTitleArc);
      const bottomTitleRect = getTitleVisualRect(bottomTitleArc);
      const nodeCenterY = waveTopInSteps + (point.y / viewBox.height) * waveRect.height;
      const nodeHalfHeight = nodeRect.height / 2;
      const isTopLabel = topBlock.classList.contains('fpw-step__top--label');
      const isBottomLabel = bottomBlock.classList.contains('fpw-step__bottom--label');
      let topBlockY = nodeCenterY - nodeHalfHeight - descriptionGap;
      let bottomBlockY = nodeCenterY + nodeHalfHeight + descriptionGap;

      if (isTopLabel && topTitleRect) {
        const titleBottomOffset = topRect.bottom - topTitleRect.bottom;
        const desiredTitleBottom = nodeCenterY - nodeHalfHeight - titleGap;
        topBlockY = desiredTitleBottom + titleBottomOffset;
      }

      if (isBottomLabel && bottomTitleRect) {
        const titleTopOffset = bottomTitleRect.top - bottomRect.top;
        const desiredTitleTop = nodeCenterY + nodeHalfHeight + titleGap;
        bottomBlockY = desiredTitleTop - titleTopOffset;
      }

      const topVisualStart = topBlockY - topRect.height;
      const bottomVisualEnd = bottomBlockY + bottomRect.height;
      const nodeVisualTop = nodeCenterY - nodeHalfHeight;
      const nodeVisualBottom = nodeCenterY + nodeHalfHeight;
      const visualLeft = Math.min(topRect.left, bottomRect.left, nodeRect.left) - stepsRect.left;
      const visualRight = Math.max(topRect.right, bottomRect.right, nodeRect.right) - stepsRect.left;

      bounds.push({
        left: visualLeft,
        right: visualRight,
        top: Math.min(topVisualStart, nodeVisualTop),
        bottom: Math.max(bottomVisualEnd, nodeVisualBottom),
        step,
        nodeCenterY,
        topBlockY,
        bottomBlockY
      });
    });

    if (!bounds.length) return;

    const topPadding = 8;
    const bottomPadding = 14;
    const pathVisualLeft = (pathBounds.x / viewBox.width) * timelineRect.width;
    const pathVisualRight = ((pathBounds.x + pathBounds.width) / viewBox.width) * timelineRect.width;
    const minLeft = Math.min(pathVisualLeft, ...bounds.map((item) => item.left));
    const maxRight = Math.max(pathVisualRight, ...bounds.map((item) => item.right));
    const minTop = Math.min.apply(null, bounds.map((item) => item.top));
    const maxBottom = Math.max.apply(null, bounds.map((item) => item.bottom));
    const horizontalShift = (stepsRect.width / 2) - ((minLeft + maxRight) / 2);
    const verticalShift = minTop < topPadding ? topPadding - minTop : 0;
    const totalHeight = maxBottom + verticalShift + bottomPadding;

    section.style.setProperty('--fpw-wave-offset-x', horizontalShift.toFixed(2) + 'px');
    section.style.setProperty('--fpw-wave-offset-y', verticalShift.toFixed(2) + 'px');
    stepsWrap.style.height = totalHeight.toFixed(2) + 'px';

    bounds.forEach((item) => {
      item.step.style.setProperty('--fpw-step-center-x', (parseFloat(item.step.style.getPropertyValue('--fpw-step-center-x')) + horizontalShift).toFixed(2) + 'px');
      item.step.style.setProperty('--fpw-node-center-y', (item.nodeCenterY + verticalShift).toFixed(2) + 'px');
      item.step.style.setProperty('--fpw-top-block-y', (item.topBlockY + verticalShift).toFixed(2) + 'px');
      item.step.style.setProperty('--fpw-bottom-block-y', (item.bottomBlockY + verticalShift).toFixed(2) + 'px');
    });
  }

  function scheduleUpdate(section) {
    if (!section) return;

    if (section._fpwFrame) {
      window.cancelAnimationFrame(section._fpwFrame);
    }

    section._fpwFrame = window.requestAnimationFrame(() => {
      updateSection(section);
    });
  }

  function observeResizeTargets(section, resizeObserver) {
    [
      section,
      section.querySelector('.fpw-timeline'),
      section.querySelector('.fpw-wave'),
      section.querySelector('.fpw-steps'),
      ...section.querySelectorAll('.fpw-step'),
      ...section.querySelectorAll('.fpw-step__top'),
      ...section.querySelectorAll('.fpw-step__bottom'),
      ...section.querySelectorAll('.fpw-step__node'),
      ...section.querySelectorAll('.fpw-step__title-arc'),
      ...section.querySelectorAll('.fpw-step__description')
    ]
      .filter(Boolean)
      .forEach((element) => resizeObserver.observe(element));
  }

  function initReveal(section) {
    const items = Array.from(section.querySelectorAll('[data-fpw-reveal]'));
    if (!items.length) return;

    section.dataset.fpwJs = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const shouldRefreshLayout = entry.target.classList.contains('fpw-wave') || entry.target.classList.contains('fpw-step');

          entry.target.classList.add('is-visible');

          if (shouldRefreshLayout) {
            window.requestAnimationFrame(() => {
              scheduleUpdate(section);
            });

            entry.target.addEventListener(
              'transitionend',
              (event) => {
                if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
                scheduleUpdate(section);
              },
              { once: true }
            );
          }

          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.16,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    items.forEach((item) => observer.observe(item));
    section._fpwRevealObserver = observer;
  }

  function initSection(section) {
    if (!section || section._fpwInitialized) {
      if (section) scheduleUpdate(section);
      return;
    }

    section._fpwInitialized = true;
    initReveal(section);

    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate(section);
    });

    observeResizeTargets(section, resizeObserver);
    section._fpwResizeObserver = resizeObserver;

    const structureObserver = new MutationObserver((mutations) => {
      let shouldRefreshObservers = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          shouldRefreshObservers = true;
        }
      });

      if (shouldRefreshObservers && section._fpwResizeObserver) {
        section._fpwResizeObserver.disconnect();
        observeResizeTargets(section, section._fpwResizeObserver);
      }

      scheduleUpdate(section);
    });

    structureObserver.observe(section, {
      childList: true,
      subtree: true
    });

    section._fpwStructureObserver = structureObserver;

    section.querySelectorAll('.fpw-step__icon-image').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => scheduleUpdate(section), { once: true });
    });

    scheduleUpdate(section);
  }

  function init(root) {
    const sections = root.matches && root.matches(selector)
      ? [root]
      : Array.from(root.querySelectorAll ? root.querySelectorAll(selector) : []);

    sections.forEach(initSection);
  }

  function updateAll() {
    document.querySelectorAll(selector).forEach((section) => {
      scheduleUpdate(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  window.addEventListener('resize', updateAll);
  window.addEventListener('load', updateAll);
  if (desktopMedia.addEventListener) {
    desktopMedia.addEventListener('change', updateAll);
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(updateAll).catch(function () {});
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
